import {
  Alert,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  FormHelperText,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type {
  ClassificationOutcome,
  ClassificationStageData,
  Contravention,
  FileDetail,
  RemedialMeasure,
} from '@munifocus/contracts-gov';
import {
  CLASSIFICATION_OUTCOMES,
  CONTRAVENTIONS,
  MEASURES_BY_CONTRAVENTION,
  classificationRoute,
} from '@munifocus/contracts-gov';
import { FileAttachments } from '../../components/FileAttachments';
import { StageLayout } from '../../components/StageLayout';
import { useSaveStage } from '../../lib/files-api';
import { useSubmitValidation } from '../../lib/useSubmitValidation';

export function ClassificationStage({ file }: { file: FileDetail }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const save = useSaveStage(file.id, 'classification');
  const initial = (file.stageData?.classification ?? {}) as ClassificationStageData;

  // The wider conclusion, which can also be "compliant" or a s. 139 emergency
  // assessment. `contravention` is derived from it rather than held separately,
  // so the two cannot contradict each other on screen — the API applies the same
  // rule when it saves.
  //
  // No default: arriving pre-set to s. 131(1) meant a file could be escalated
  // under a subsection the officer never chose. The outcome is the decision
  // this stage records, so it starts empty and Complete waits for it.
  const [outcome, setOutcome] = useState<ClassificationOutcome | ''>(
    initial.classificationOutcome ?? initial.contravention ?? '',
  );
  const contravention = CONTRAVENTIONS.includes(outcome as Contravention)
    ? (outcome as Contravention)
    : undefined;
  const [expertReportRef, setExpertReportRef] = useState(initial.expertReportRef ?? '');
  const [correctiveWork, setCorrectiveWork] = useState(initial.correctiveWork ?? '');
  const [complianceDeadline, setComplianceDeadline] = useState(
    initial.complianceDeadline?.slice(0, 10) ?? '',
  );
  const [emergency, setEmergency] = useState(initial.emergency ?? file.emergency);
  const [remedialMeasures, setRemedialMeasures] = useState<RemedialMeasure[]>(
    initial.remedialMeasures ?? [],
  );
  const [anticipatedCost, setAnticipatedCost] = useState(initial.anticipatedCost ?? '');
  const [objectValue, setObjectValue] = useState(initial.objectValue ?? '');
  const [timeToCompleteDays, setTimeToCompleteDays] = useState(
    initial.timeToCompleteDays !== undefined ? String(initial.timeToCompleteDays) : '',
  );
  const [returnInspectionDate, setReturnInspectionDate] = useState(
    initial.returnInspectionDate?.slice(0, 10) ?? '',
  );

  // Which measures this subsection can carry, and the s. 131(1)-only object
  // value. Both mirror what the API enforces; keeping the form in step means the
  // officer never fills in a field the server is about to drop.
  const allowedMeasures = contravention ? MEASURES_BY_CONTRAVENTION[contravention] : [];
  const showObjectValue = contravention === 'lga_131_1';
  const showAssessment = contravention !== undefined;

  // Where this classification sends the file — stated before the officer
  // commits, because two of the five outcomes close it outright.
  const route = outcome ? classificationRoute(outcome, emergency) : null;

  const toggleMeasure = (measure: RemedialMeasure) =>
    setRemedialMeasures((prev) =>
      prev.includes(measure) ? prev.filter((m) => m !== measure) : [...prev, measure],
    );

  /**
   * Changing the outcome drops any measure the new subsection cannot carry.
   * Same rule as hiding an input having to drop its value: a clean-up ticked
   * under s. 131(1) is invisible after switching to s. 131(2), and leaving it in
   * state would show it in the stage record and send it — the server prunes it,
   * so the officer would be told one thing and the file would hold another.
   */
  const changeOutcome = (next: ClassificationOutcome) => {
    setOutcome(next);
    const nextAllowed = CONTRAVENTIONS.includes(next as Contravention)
      ? MEASURES_BY_CONTRAVENTION[next as Contravention]
      : [];
    setRemedialMeasures((prev) => prev.filter((m) => nextAllowed.includes(m)));
    if (next !== 'lga_131_1') setObjectValue('');
  };

  // s.131(3) requires an expert report before the file can proceed (expert gate).
  // The gate is satisfied by an attached report or, failing that, by the
  // free-text reference — files opened before attachments existed carry only a
  // reference, and blocking those would strand them mid-workflow.
  const expertRequired = outcome === 'lga_131_3';
  const [expertReportCount, setExpertReportCount] = useState(0);
  const expertSatisfied = expertReportCount > 0 || expertReportRef.trim().length > 0;
  // A compliant finding closes the file and asks for no corrective work — there
  // is none. Requiring it there would force the officer to invent one.
  const v = useSubmitValidation({
    outcome: outcome !== '' ? null : t('common.validation.choose'),
    expertReportRef:
      !expertRequired || expertSatisfied ? null : t('gov.classification.expertRequired'),
    correctiveWork:
      route === 'close' || correctiveWork.trim() ? null : t('common.validation.required'),
  });

  const onComplete = () =>
    save.mutate(
      {
        classificationOutcome: outcome || undefined,
        contravention,
        expertReportRef: expertReportRef.trim() || undefined,
        correctiveWork,
        complianceDeadline: complianceDeadline || undefined,
        emergency,
        // Only sent for a subsection that can carry them; the API prunes too,
        // but sending a value the officer could not see would be dishonest.
        remedialMeasures: showAssessment ? remedialMeasures : undefined,
        anticipatedCost: showAssessment ? anticipatedCost || undefined : undefined,
        objectValue: showObjectValue ? objectValue || undefined : undefined,
        timeToCompleteDays:
          showAssessment && timeToCompleteDays ? Number(timeToCompleteDays) : undefined,
        returnInspectionDate: showAssessment ? returnInspectionDate || undefined : undefined,
        advance: true,
      },
      { onSuccess: () => navigate(`/files/${file.id}`) },
    );

  const record = [
    {
      label: t('gov.stage4.outcome.label'),
      value: outcome ? t(`gov.stage4.outcome.options.${outcome}`) : '',
    },
    ...(showAssessment
      ? [
          {
            label: t('gov.stage4.assessment.measures'),
            value: remedialMeasures.map((m) => t(`gov.stage4.assessment.measure.${m}`)).join(', '),
          },
          { label: t('gov.stage4.assessment.anticipatedCost'), value: anticipatedCost },
          ...(showObjectValue
            ? [{ label: t('gov.stage4.assessment.objectValue'), value: objectValue }]
            : []),
          {
            label: t('gov.stage4.assessment.timeToComplete'),
            value: timeToCompleteDays
              ? `${timeToCompleteDays} ${t('gov.stage4.assessment.timeToCompleteUnit')}`
              : '',
          },
          {
            label: t('gov.stage4.assessment.returnInspectionDate'),
            value: returnInspectionDate,
          },
        ]
      : []),
    ...(expertRequired
      ? [{ label: t('gov.classification.expertReportRef'), value: expertReportRef }]
      : []),
    { label: t('gov.classification.correctiveWork'), value: correctiveWork, block: true },
    { label: t('gov.classification.complianceDeadline'), value: complianceDeadline },
    {
      label: t('gov.status.emergency'),
      value: emergency ? t('common.yes') : t('common.no'),
    },
  ];

  return (
    <StageLayout
      file={file}
      stageId="classification"
      onComplete={v.guard(onComplete)}
      saving={save.isPending}
      record={record}
    >
      {save.isError && <Alert severity="error">{t('gov.stage.saveError')}</Alert>}

      <FormControl fullWidth size="small" error={Boolean(v.errors.outcome)}>
        <InputLabel>{t('gov.stage4.outcome.label')}</InputLabel>
        <Select
          label={t('gov.stage4.outcome.label')}
          aria-label={t('gov.stage4.outcome.label')}
          inputRef={v.register('outcome')}
          value={outcome}
          onChange={(e) => changeOutcome(e.target.value as ClassificationOutcome)}
        >
          {CLASSIFICATION_OUTCOMES.map((o) => (
            <MenuItem key={o} value={o}>
              {/* The statutory effect sits under each option: two of the five
                  close the file outright, which the subsection number alone
                  does not convey. Stacked because the French runs longer. */}
              <Stack spacing={0.25} sx={{ py: 0.5, whiteSpace: 'normal' }}>
                <Typography variant="body2">{t(`gov.stage4.outcome.options.${o}`)}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(`gov.stage4.outcome.descriptions.${o}`)}
                </Typography>
              </Stack>
            </MenuItem>
          ))}
        </Select>
        {v.errors.outcome && <FormHelperText>{v.errors.outcome}</FormHelperText>}
      </FormControl>

      <Typography variant="caption" color="text.secondary">
        {t('gov.stage4.outcome.hint')}
      </Typography>

      {/* Stated before the officer commits: a compliant finding, or a s. 139
          analysis with no emergency, closes the file rather than escalating it. */}
      {route && (
        <Alert severity={route === 'emergency' ? 'error' : route === 'close' ? 'success' : 'info'}>
          {t(`gov.stage4.route.${route}`)}
        </Alert>
      )}

      {expertRequired && (
        <>
          <Alert severity={expertSatisfied ? 'info' : 'warning'}>
            {t('gov.classification.expertGate')}
          </Alert>
          <FileAttachments
            fileId={file.id}
            kind="expert_report"
            onCountChange={setExpertReportCount}
          />
          <TextField
            fullWidth
            size="small"
            label={t('gov.classification.expertReportRef')}
            {...v.field('expertReportRef')}
            helperText={v.errors.expertReportRef ?? t('gov.classification.expertReportRefHelp')}
            value={expertReportRef}
            onChange={(e) => setExpertReportRef(e.target.value)}
          />
        </>
      )}

      {showAssessment && (
        <>
          <Divider />
          <Typography variant="overline">{t('gov.stage4.assessment.title')}</Typography>

          <FormControl component="fieldset">
            <Typography variant="body2" component="legend">
              {t('gov.stage4.assessment.measures')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('gov.stage4.assessment.measuresHint')}
            </Typography>
            {/* Only the measures this subsection can carry: s. 131(1) is repair
                or clean up, s. 131(2) repair or demolish, s. 131(3) demolish. */}
            {allowedMeasures.map((m) => (
              <FormControlLabel
                key={m}
                control={
                  <Checkbox
                    checked={remedialMeasures.includes(m)}
                    onChange={() => toggleMeasure(m)}
                  />
                }
                label={t(`gov.stage4.assessment.measure.${m}`)}
              />
            ))}
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label={t('gov.stage4.assessment.anticipatedCost')}
              value={anticipatedCost}
              onChange={(e) => setAnticipatedCost(e.target.value)}
            />
            {/* "the potential value of the objects" — s. 131(1) only. */}
            {showObjectValue && (
              <TextField
                fullWidth
                size="small"
                label={t('gov.stage4.assessment.objectValue')}
                helperText={t('gov.stage4.assessment.objectValueHint')}
                value={objectValue}
                onChange={(e) => setObjectValue(e.target.value)}
              />
            )}
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              type="number"
              label={t('gov.stage4.assessment.timeToComplete')}
              helperText={t('gov.stage4.assessment.timeToCompleteHint')}
              slotProps={{ htmlInput: { min: 1, max: 3650 } }}
              value={timeToCompleteDays}
              onChange={(e) => setTimeToCompleteDays(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('gov.stage4.assessment.returnInspectionDate')}
              helperText={t('gov.stage4.assessment.returnInspectionHint')}
              slotProps={{ inputLabel: { shrink: true } }}
              value={returnInspectionDate}
              onChange={(e) => setReturnInspectionDate(e.target.value)}
            />
          </Stack>
          <Divider />
        </>
      )}

      {/* A compliant finding closes the file; there is no corrective work. */}
      {route !== 'close' && (
        <TextField
          fullWidth
          multiline
          minRows={3}
          required
          label={t('gov.classification.correctiveWork')}
          {...v.field('correctiveWork')}
          value={correctiveWork}
          onChange={(e) => setCorrectiveWork(e.target.value)}
        />
      )}

      {/* Nothing to comply with on a file that closes here. */}
      {route !== 'close' && (
        <TextField
          fullWidth
          size="small"
          type="date"
          label={t('gov.classification.complianceDeadline')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={complianceDeadline}
          onChange={(e) => setComplianceDeadline(e.target.value)}
        />
      )}

      {/* Always available, because it is what turns a s. 139 analysis from a
          closure into the emergency route. It does not override a `compliant`
          finding: there is no contravention there for an emergency to
          accelerate, so that outcome closes the file either way. */}
      <FormControlLabel
        control={<Checkbox checked={emergency} onChange={(e) => setEmergency(e.target.checked)} />}
        label={t('gov.classification.emergency')}
      />
      {emergency && <Alert severity="error">{t('gov.classification.emergencyNote')}</Alert>}
    </StageLayout>
  );
}
